<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import { readMainPersistedAuthUidSafe } from '@/utils/mainAuthIdb'
import { getSwAuthSyncMetrics, resetSwAuthSyncMetrics } from '@/utils/swAuthSyncMetricsCore'
import { notifySwAuthClear, notifySwAuthSync } from '@/utils/swAuthSync'
import {
  clearSwAuthDebugEvents,
  formatSwAuthTelemetryEvent,
  getSwAuthDebugEvents,
} from '@/utils/swAuthDebugBuffer'
import {
  clearDevConsoleLines,
  getDevConsoleLines,
  installDevConsoleCapture,
} from '@/utils/devConsoleCapture'
import { useServiceWorkerRegistered } from '@/utils/registerSW'
import { getReloadLoopSessionInfo } from '@/utils/swAuthDebugSnapshot'

const authStore = useAuthStore()
const { user, tokenIssuedAtTime } = storeToRefs(authStore)

const {
  isServiceWorkerRegistered,
  swRegistration,
  swController,
  swActive,
  swWaiting,
  swInstalling,
} = useServiceWorkerRegistered()

const idbUid = ref<string | null | 'error'>(null)
const idbReadError = ref<string | null>(null)
const refreshing = ref(false)
const copyStatus = ref<string | null>(null)

const telemetryEvents = ref(getSwAuthDebugEvents())
const consoleLines = ref(getDevConsoleLines())
let refreshTimer: ReturnType<typeof setInterval> | undefined

const metrics = computed(() => getSwAuthSyncMetrics())

const swScriptUrl = computed(() => {
  const worker = swController.value ?? swActive.value
  return worker?.scriptURL ?? '(none)'
})

const snapshot = computed(() =>
  getReloadLoopSessionInfo({
    mainUid: user.value?.uid ?? null,
    mainIssuedAtMs: tokenIssuedAtTime.value?.getTime() ?? null,
    idbUid: idbUid.value === 'error' ? null : idbUid.value,
    idbReadError: idbReadError.value,
    swRegistered: isServiceWorkerRegistered.value,
    swScriptUrl: swScriptUrl.value,
    swControllerState: swController.value?.state ?? null,
    swActiveState: swActive.value?.state ?? null,
    swWaitingState: swWaiting.value?.state ?? null,
    swInstallingState: swInstalling.value?.state ?? null,
    hasRegistration: Boolean(swRegistration.value),
    metrics: metrics.value,
    telemetryEvents: telemetryEvents.value,
    consoleLines: consoleLines.value,
  }),
)

async function refreshIdbUid() {
  const result = await readMainPersistedAuthUidSafe()
  if (result.status === 'error') {
    idbUid.value = 'error'
    idbReadError.value = result.message
    return
  }
  idbUid.value = result.uid
  idbReadError.value = null
}

async function refreshAll() {
  refreshing.value = true
  try {
    await refreshIdbUid()
    telemetryEvents.value = [...getSwAuthDebugEvents()]
    consoleLines.value = [...getDevConsoleLines()]
  } finally {
    refreshing.value = false
  }
}

async function forceSwSync() {
  const uid = user.value?.uid
  const issuedAtMs = tokenIssuedAtTime.value?.getTime()
  if (uid && issuedAtMs != null) {
    await notifySwAuthSync({ uid, issuedAtMs })
  } else {
    await notifySwAuthSync()
  }
  await refreshAll()
}

function forceSwClear() {
  notifySwAuthClear()
  void refreshAll()
}

function resetMetrics() {
  resetSwAuthSyncMetrics()
  void refreshAll()
}

function clearLogs() {
  clearSwAuthDebugEvents()
  clearDevConsoleLines()
  void refreshAll()
}

async function copyReport() {
  copyStatus.value = null
  try {
    await navigator.clipboard.writeText(snapshot.value)
    copyStatus.value = 'Copied'
  } catch {
    copyStatus.value = 'Copy failed — select text below'
  }
}

onMounted(() => {
  installDevConsoleCapture()
  void refreshAll()
  refreshTimer = setInterval(() => {
    telemetryEvents.value = [...getSwAuthDebugEvents()]
    consoleLines.value = [...getDevConsoleLines()]
  }, 1500)
})

onUnmounted(() => {
  if (refreshTimer !== undefined) {
    clearInterval(refreshTimer)
    refreshTimer = undefined
  }
})
</script>

<template>
  <div class="space-y-4 text-sm">
    <p class="text-base-content/80">
      On-device SW auth diagnostics (no USB / remote inspector). Open with
      <code class="text-xs bg-base-200 px-1 rounded">?debugtools=true</code>, sign in, reproduce the
      issue, then <strong>Copy report</strong> and paste on your desktop.
    </p>

    <div class="flex flex-wrap gap-2">
      <button type="button" class="btn btn-sm btn-outline" :disabled="refreshing" @click="refreshAll">
        {{ refreshing ? 'Refreshing…' : 'Refresh' }}
      </button>
      <button type="button" class="btn btn-sm btn-outline" @click="forceSwSync">Force SW sync</button>
      <button type="button" class="btn btn-sm btn-outline" @click="forceSwClear">Force SW clear</button>
      <button type="button" class="btn btn-sm btn-outline" @click="resetMetrics">Reset counters</button>
      <button type="button" class="btn btn-sm btn-outline" @click="clearLogs">Clear logs</button>
      <button type="button" class="btn btn-sm btn-primary" @click="copyReport">Copy report</button>
    </div>
    <p v-if="copyStatus" class="text-xs text-base-content/70">{{ copyStatus }}</p>

    <div class="rounded-lg border border-base-300 bg-base-100 p-3">
      <h4 class="font-semibold mb-2">Live state</h4>
      <pre class="text-xs whitespace-pre-wrap break-all font-mono">{{ snapshot }}</pre>
    </div>

    <div class="rounded-lg border border-base-300 bg-base-100 p-3">
      <h4 class="font-semibold mb-2">SW auth telemetry (newest first)</h4>
      <ul v-if="telemetryEvents.length" class="text-xs font-mono space-y-1 max-h-48 overflow-y-auto">
        <li v-for="(row, index) in [...telemetryEvents].reverse()" :key="`${row.at}-${index}`">
          {{ row.atLabel }} — {{ formatSwAuthTelemetryEvent(row.event) }}
        </li>
      </ul>
      <p v-else class="text-xs text-base-content/60">No events yet. Sign in or tap Force SW sync.</p>
    </div>

    <div class="rounded-lg border border-base-300 bg-base-100 p-3">
      <h4 class="font-semibold mb-2">Captured console (this tab)</h4>
      <p class="text-xs text-base-content/60 mb-2">
        Starts when you open Dev Settings. Does not include logs from before this session.
      </p>
      <ul v-if="consoleLines.length" class="text-xs font-mono space-y-1 max-h-40 overflow-y-auto">
        <li v-for="(line, index) in [...consoleLines].reverse()" :key="index">{{ line }}</li>
      </ul>
      <p v-else class="text-xs text-base-content/60">No console lines captured yet.</p>
    </div>
  </div>
</template>
